export interface GoogleAdsStripeBillingDetails {
  name?: string | null;
  address?: {
    line1?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country?: string | null;
  } | null;
}

export interface GoogleAdsGeoData {
  country?: string | null;
  city?: string | null;
  region?: string | null;
}

export interface GoogleAdsUserDataAddress {
  first_name: string;
  last_name: string;
  postal_code: string;
  country: string;
  street?: string;
  city?: string;
  region?: string;
}

export interface GoogleAdsUserData {
  email?: string;
  address?: GoogleAdsUserDataAddress;
}

export const GOOGLE_ADS_BILLING_DETAILS_KEY = 'google_ads_billing_details';

interface BuildGoogleAdsUserDataInput {
  email?: string | null;
  profileName?: string | null;
  stripeBillingDetails?: GoogleAdsStripeBillingDetails | null;
  geo?: GoogleAdsGeoData | null;
}

const splitName = (name?: string | null) => {
  const trimmed = name?.trim();
  if (!trimmed) return { firstName: '', lastName: '' };

  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0] || '',
    lastName: parts.slice(1).join(' ') || '',
  };
};

const nonEmpty = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed || undefined;
};

export const buildGoogleAdsUserData = ({
  email,
  profileName,
  stripeBillingDetails,
  geo,
}: BuildGoogleAdsUserDataInput): GoogleAdsUserData => {
  const userData: GoogleAdsUserData = {};

  const normalizedEmail = email?.trim().toLowerCase();
  if (normalizedEmail) {
    userData.email = normalizedEmail;
  }

  const profileParts = splitName(profileName);
  const stripeParts = splitName(stripeBillingDetails?.name);
  const firstName = stripeParts.firstName || profileParts.firstName;
  const lastName = stripeParts.lastName || profileParts.lastName;

  const stripeAddress = stripeBillingDetails?.address;
  const postalCode = nonEmpty(stripeAddress?.postal_code);
  const country = nonEmpty(stripeAddress?.country) || nonEmpty(geo?.country);
  const city = nonEmpty(stripeAddress?.city) || nonEmpty(geo?.city);
  const region = nonEmpty(stripeAddress?.state) || nonEmpty(geo?.region);
  const street = nonEmpty(stripeAddress?.line1);

  if (firstName && lastName && postalCode && country) {
    userData.address = {
      first_name: firstName,
      last_name: lastName,
      postal_code: postalCode,
      country,
    };

    if (street) userData.address.street = street;
    if (city) userData.address.city = city;
    if (region) userData.address.region = region;
  }

  return userData;
};

export const hasGoogleAdsUserData = (userData: GoogleAdsUserData) =>
  Boolean(userData.email || userData.address);
